import {
  CreateFormTemplateFieldRequest,
  FormTemplateFieldConditionalRule,
  MAX_FORM_TEMPLATE_FIELD_NAME_LENGTH,
  NonEmptyArray,
} from 'mapistry-shared';
import { useCallback } from 'react';
import {
  EditableFormTemplateField,
  EditableFormTemplateFieldGroup,
  FieldGroupSlug,
  isTriggerField,
  TriggerField,
} from '../../types';
import { FieldConditionHelpers } from '../useFieldConditionHelpers';
import { getFieldCopies } from './newFieldHelpers';

const getGenericErrorMessage = (
  fieldToCopy: EditableFormTemplateField,
): string =>
  `Something went wrong when copying the field "${fieldToCopy.name}"`;

const getConditionalCycleErrorMessage = (
  fieldNames: (string | undefined)[],
): string =>
  `Cannot copy due to a circular condition cycle on the fields.
  Please check and correct the chain of conditions for this field.
  Fields in the cycle: ${fieldNames
    .filter((name) => name !== undefined)
    .map((name) => `"${name}"`)
    .join(', ')}`;

const getCopiedFieldName = (originalFieldName: string): string =>
  `${originalFieldName} (copy)`.slice(0, MAX_FORM_TEMPLATE_FIELD_NAME_LENGTH);

const getFieldAndDependentConditionalFieldsIds = (
  currentFieldId: string,
  fieldConditionHelpers: FieldConditionHelpers,
  fieldsToCopy: Set<string> = new Set(),
): Set<string> => {
  const currentField = fieldConditionHelpers.getFieldById(currentFieldId);
  if (!currentField) return fieldsToCopy;

  fieldsToCopy.add(currentField.id);

  if (!isTriggerField(currentField)) {
    return fieldsToCopy;
  }

  currentField.conditionalRules.forEach(
    (rule: FormTemplateFieldConditionalRule) =>
      (rule.conditionallyShownFields ?? []).forEach(({ id }) => {
        if (fieldsToCopy.has(id)) {
          const fieldNames = [...fieldsToCopy].map(
            (fieldId) => fieldConditionHelpers.getFieldById(fieldId)?.name,
          );
          throw new Error(getConditionalCycleErrorMessage(fieldNames));
        }

        getFieldAndDependentConditionalFieldsIds(
          id,
          fieldConditionHelpers,
          fieldsToCopy,
        );
      }),
  );

  return fieldsToCopy;
};

// Copy to the parent's conditions: If the copied field is conditional on another field,
// add the new field to the conditional rule that the original field is conditional on
const addToTriggerFieldCondition = (
  originalField: EditableFormTemplateField,
  copiedField: EditableFormTemplateField,
  fieldConditionHelpers: FieldConditionHelpers,
  updateTriggerField: (field: TriggerField) => void,
) => {
  const triggerField =
    fieldConditionHelpers.getTriggerFieldByConditionallyShownFieldId(
      originalField.id,
    );

  if (!triggerField) return;

  let originalHideInPdfValue = false;

  const ruleIdx = triggerField.conditionalRules.findIndex((rule) =>
    rule.conditionallyShownFields.some(
      ({ id, hideInPdfIfConditionallyHidden }) => {
        if (id === originalField.id) {
          originalHideInPdfValue = hideInPdfIfConditionallyHidden;
          return true;
        }
        return false;
      },
    ),
  );

  const updatedTriggerField = {
    ...triggerField,
    conditionalRules: triggerField.conditionalRules.map((rule, index) => {
      if (index !== ruleIdx) return rule;
      return {
        ...rule,
        conditionallyShownFields: [
          ...rule.conditionallyShownFields,
          {
            id: copiedField.id,
            hideInPdfIfConditionallyHidden: originalHideInPdfValue,
          },
        ],
      };
    }) as NonEmptyArray<FormTemplateFieldConditionalRule>,
  };

  updateTriggerField(updatedTriggerField);
};

export const useCopyField = ({
  activeGroupSlug,
  addFieldsAtIndex,
  fieldConditionHelpers,
  groups,
  updateField,
}: {
  activeGroupSlug: FieldGroupSlug | undefined;
  addFieldsAtIndex: (
    newFields: CreateFormTemplateFieldRequest[],
    index: number,
  ) => EditableFormTemplateField[];
  fieldConditionHelpers: FieldConditionHelpers;
  groups: EditableFormTemplateFieldGroup[];
  updateField: (
    updatedField: EditableFormTemplateField,
    groupSlug?: FieldGroupSlug,
  ) => void;
}) => {
  const copyField = useCallback(
    (fieldToCopy: EditableFormTemplateField): void => {
      const fieldIdsToCopy = getFieldAndDependentConditionalFieldsIds(
        fieldToCopy.id,
        fieldConditionHelpers,
      );

      const activeFields = groups.find(
        (g) => g.slug === activeGroupSlug,
      )?.fields;

      const genericError = getGenericErrorMessage(fieldToCopy);

      if (!activeFields) throw new Error(genericError);

      const { fieldCopies, originalIdsToNewFields } = getFieldCopies({
        fields: activeFields.filter((field) => fieldIdsToCopy.has(field.id)),
        fieldValuesMapper: (field) => ({
          ...field,
          name: getCopiedFieldName(field.name),
          attributes: [],
          isDuplicableDisplayField: false,
        }),
        errorMessage: genericError,
      });

      const newCopiedField = originalIdsToNewFields.get(fieldToCopy.id);

      if (!newCopiedField) throw new Error(genericError);

      let lastFieldToCopyIdx = 0;
      activeFields.forEach((field, index) => {
        if (fieldIdsToCopy.has(field.id)) lastFieldToCopyIdx = index;
      });

      addFieldsAtIndex(fieldCopies, lastFieldToCopyIdx + 1);

      addToTriggerFieldCondition(
        fieldToCopy,
        newCopiedField,
        fieldConditionHelpers,
        updateField,
      );
    },
    [
      activeGroupSlug,
      addFieldsAtIndex,
      fieldConditionHelpers,
      groups,
      updateField,
    ],
  );

  return copyField;
};
